import posthog from "posthog-js"
import { captureError } from "@/shared/lib/diagnostics"
import { detectPlatform } from "@/shared/lib/platform"
import { clearSessionScope, survivesLogout } from "@/shared/lib/storage"
import { bumpSessionVersion } from "./session-version"
import { clearTokens, getOwnerId, setOwnerId } from "./token-storage"
import { clearStoredUserId } from "./user-id-storage"

type SessionExpiredListener = () => void

const sessionExpiredListeners = new Set<SessionExpiredListener>()

/**
 * Subscribes to session loss outside the user's control — no refresh token, or
 * the server rejected it. The router listens and re-runs authentication.
 *
 * @returns unsubscribe function
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
	sessionExpiredListeners.add(listener)
	return () => {
		sessionExpiredListeners.delete(listener)
	}
}

/** Called from `doTokenRefresh` once the stored tokens are known to be dead. */
export function notifySessionExpired(): void {
	for (const listener of sessionExpiredListeners) listener()
}

type SessionCleanup = () => void | Promise<void>
const sessionCleanups = new Set<SessionCleanup>()
let clearing: Promise<void> | null = null

/**
 * Registers in-memory state that has to die with the session — module-level
 * caches outside React, which `clearSession` cannot reach on its own. The
 * owning module registers at load, next to the state it resets.
 */
export function registerSessionCleanup(cleanup: SessionCleanup): () => void {
	sessionCleanups.add(cleanup)
	return () => {
		sessionCleanups.delete(cleanup)
	}
}

// Скоупы своих ключей описаны в `shared/lib/storage`: session-ключи уходят
// везде. В Telegram логаута нет, а в sessionStorage лежит кэш launch-параметров
// SDK (`tapps/*`) — без него retrieveLaunchParams отваливается до перезапуска,
// поэтому чужие ключи там не трогаем.
function clearWebStorage(): void {
	clearSessionScope()
	if (detectPlatform() === "telegram") return
	const preserved = Object.keys(localStorage)
		.filter(survivesLogout)
		.map((key) => [key, localStorage.getItem(key)] as const)
	localStorage.clear()
	sessionStorage.clear()
	for (const [key, value] of preserved) {
		if (value !== null) localStorage.setItem(key, value)
	}
}

/**
 * Полная очистка локальных данных: токены и user_id (хранилище выбирает
 * `user-id-storage`, localStorage чистится всегда) и `luni:session:*`. Везде,
 * кроме Telegram, дополнительно вычищаются localStorage и sessionStorage —
 * кроме `luni:device:*` и `luni:user:*` из `shared/lib/storage`; после выхода
 * приложение открывается как с нуля.
 *
 * Само по себе не уводит с текущего экрана: вызывающий решает, редиректить
 * ли на онбординг (logout) или поднять `notifySessionExpired` и отдать
 * решение роутеру (протухший refresh-токен в `doTokenRefresh`).
 */
export function clearSession(): Promise<void> {
	if (clearing) return clearing
	// Invalidates pending token writes immediately. The other cleanups run even
	// if storage fails; a failed UI reset must not prevent transport shutdown.
	bumpSessionVersion()
	const tokens = clearTokens()
	const cleanups: SessionCleanup[] = [
		clearStoredUserId,
		clearWebStorage,
		...sessionCleanups,
	]
	clearing = Promise.allSettled([
		tokens,
		...cleanups.map((cleanup) => Promise.resolve().then(cleanup)),
	])
		.then((results) => {
			for (const result of results) {
				if (result.status === "rejected") {
					captureError(result.reason, { scope: "session.cleanup" })
				}
			}
			posthog.reset()
		})
		.finally(() => {
			clearing = null
		})
	return clearing
}

let accountQueue: Promise<void> = Promise.resolve()

/** Reconcile outside the token queue: clearSession itself queues token removal. */
export function reconcileAccount(ownerId: string | null): Promise<void> {
	if (ownerId === null) return Promise.resolve()
	const operation = accountQueue.then(async () => {
		const previousOwnerId = await getOwnerId()
		if (previousOwnerId && previousOwnerId !== ownerId) await clearSession()
		await setOwnerId(ownerId)
	})
	accountQueue = operation.catch(() => {})
	return operation
}
